import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import { type CalendarViewMode } from '@apps/web/components/shifts_calendar/calendar_model';
import { ShiftCalendarPane } from '@apps/web/components/shifts_calendar/shift_calendar_pane';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const calendarSearchSchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    view: z.enum(['day', 'week', 'month']).optional(),
    // Month-only scope (`caregiver:<uuid>` / `client:<uuid>`); the calendar
    // pane validates the value and falls back to the organization month.
    scope: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export const Route = createFileRoute('/_app/(main)/calendar/')({
    component: CalendarPage,
    validateSearch: calendarSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Calendar | Phoebe',
            },
        ],
    }),
});

function CalendarPage() {
    const navigate = useNavigate();
    const currentSearch = Route.useSearch();
    const {
        from,
        to,
        view,
        scope,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = currentSearch;
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, { reason: 'calendar-page' });
    const agentV3Enabled = useFeatureFlag(
        'agent_v3_enabled',
        organization?.features,
    );
    // Under v3 the calendar lives on `/shifts`, which is the route Phoebe's
    // already-sent Slack, email, and SMS links point at. Redirect rather than
    // render so there is one scheduling destination.
    const shouldRedirectToShifts =
        !isWaitingForDeepLinkOrganization &&
        !isLoadingOrganization &&
        agentV3Enabled;

    useEffect(() => {
        if (!shouldRedirectToShifts) {
            return;
        }
        void navigate({
            to: '/shifts',
            search: { ...currentSearch, presentation: 'calendar' },
            replace: true,
        });
    }, [shouldRedirectToShifts, currentSearch, navigate]);

    if (
        isWaitingForDeepLinkOrganization ||
        isLoadingOrganization ||
        shouldRedirectToShifts
    ) {
        return null;
    }

    function handleCalendarRangeChange(from: string, to: string): void {
        void navigate({
            to: '/calendar',
            search: {
                ...currentSearch,
                from,
                to,
            },
            replace: true,
        });
    }

    function handleCalendarViewChange(
        view: CalendarViewMode,
        from: string,
        to: string,
    ): void {
        void navigate({
            to: '/calendar',
            search: {
                ...currentSearch,
                view,
                from,
                to,
            },
        });
    }

    function handleCalendarScopeChange(
        scope: string | null,
        options?: { readonly replace?: boolean },
    ): void {
        void navigate({
            to: '/calendar',
            search: {
                ...currentSearch,
                scope: scope ?? undefined,
            },
            replace: options?.replace ?? false,
        });
    }

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 max-md:[&>div:first-child]:!h-full">
                <ShiftCalendarPane
                    initialFrom={from}
                    initialTo={to}
                    view={view ?? 'week'}
                    viewExplicit={view !== undefined}
                    scope={scope}
                    onRangeChange={handleCalendarRangeChange}
                    onViewChange={handleCalendarViewChange}
                    onScopeChange={handleCalendarScopeChange}
                />
            </div>
        </div>
    );
}
